import type { SyntheticEvent } from 'react';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { PriceBookSheetRowView } from '@/document-render/domain/priceBook/buildPriceBookBySheetView';
import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import { CanvasEyeButton } from '@/document-render/presentation/editing/quote/CanvasEyeButton';
import { PriceBookActionsMenu } from './PriceBookActionsMenu';

export interface PriceBookRowHandle {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

export interface PriceBookRowControlsProps {
  row: PriceBookSheetRowView;
  editing: LiveDocumentEditingConfig;
  handle: PriceBookRowHandle;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

/**
 * Fixed rows (hourly, travel, labor…) only hide/show. Custom line items get a drag grip and a
 * Duplicate / Delete / Move menu; they reorder within their own sheet.
 */
export function PriceBookRowControls({ row, editing, handle, onMoveUp, onMoveDown, canMoveUp, canMoveDown }: PriceBookRowControlsProps) {
  if (row.rowKind !== 'lineItem') {
    if (!editing.onTogglePriceBookRowHidden) return null;
    const visible = !row.hidden;
    return (
      <span onClick={stop} onDoubleClick={stop} role="presentation">
        <CanvasEyeButton
          label={`${visible ? 'Hide' : 'Show'} ${row.name}`}
          pressed={visible}
          onToggle={() => editing.onTogglePriceBookRowHidden?.(row.id, row.hidden)}
        />
      </span>
    );
  }

  const canReorder = Boolean(editing.onReorderPriceBookLineItems);
  const duplicate = editing.onDuplicatePriceBookRow;
  const remove = editing.onDeletePriceBookRow;
  return (
    <>
      {canReorder ? (
        <button
          type="button"
          ref={handle.setActivatorNodeRef}
          aria-label={`Reorder ${row.name}`}
          data-canvas-affordance="grip"
          {...handle.attributes}
          {...handle.listeners}
          onClick={stop}
          onDoubleClick={stop}
          style={{ cursor: 'grab' }}
        >
          ⠿
        </button>
      ) : null}
      <PriceBookActionsMenu
        label={`More actions - ${row.name}`}
        actions={[
          { label: 'Duplicate', onSelect: duplicate ? () => duplicate(row.id) : undefined },
          { label: 'Delete', onSelect: remove ? () => remove(row.id) : undefined },
          { label: 'Move up', onSelect: canReorder ? onMoveUp : undefined, disabled: !canMoveUp },
          { label: 'Move down', onSelect: canReorder ? onMoveDown : undefined, disabled: !canMoveDown }
        ]}
      />
    </>
  );
}
